import React, { useMemo, useState } from 'react';
import { Modal, TextField, SelectField, Button } from '@dopomogai/ui';
import { useShares } from '@dopomogai/agent-kit';
import type { ShareResourceType, ShareRole } from '@dopomogai/types';
import { useTranslation } from 'react-i18next';
import { toast } from '@dopomogai/ui';

interface ShareResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: ShareResourceType;
  resourceId: string;
  resourceLabel?: string | null;
  onShared?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export const ShareResourceModal: React.FC<ShareResourceModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  resourceId,
  resourceLabel,
  onShared,
}) => {
  const { t } = useTranslation('agentBuilder');
  const { shareResource } = useShares();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ShareRole>('viewer');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resourceTypeLabel = useMemo(() => {
    if (resourceType === 'agent') return t('shareModal.resource.agent', 'Agent');
    return t('shareModal.resource.widget_version', 'Widget Version');
  }, [resourceType, t]);

  const roleOptions = useMemo(
    () => [
      { value: 'viewer', label: t('shareModal.roles.viewer', 'Viewer') },
      { value: 'editor', label: t('shareModal.roles.editor', 'Editor') },
    ],
    [t],
  );

  const handleSubmit = async () => {
    setError(null);
    const trimmedEmail = email.trim();

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError(t('shareModal.invalidEmail', 'Enter a valid email address.'));
      return;
    }

    setIsSubmitting(true);
    try {
      await shareResource({
        resource_type: resourceType,
        resource_id: resourceId,
        user_email: trimmedEmail,
        role,
      });
      toast.success(t('shareModal.success', 'Invitation sent!'));
      setEmail('');
      setRole('viewer');
      onShared?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || t('shareModal.error', 'Failed to share resource.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('shareModal.title', 'Share {{resource}}', { resource: resourceTypeLabel })}
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            text={t('shareModal.cancelButton', 'Cancel')}
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            variant="primary"
            text={t('shareModal.shareButton', 'Share')}
            onClick={handleSubmit}
            disabled={isSubmitting}
          />
        </div>
      }
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {t('shareModal.description', 'Invite a teammate by email to view or edit this resource.')}
        </p>

        {resourceLabel && (
          <p className="text-xs text-slate-500">
            {t('shareModal.resourceLabel', '{{type}}: {{label}}', {
              type: resourceTypeLabel,
              label: resourceLabel,
            })}
          </p>
        )}

        <TextField
          label={t('shareModal.emailLabel', 'Email address')}
          placeholder={t('shareModal.emailPlaceholder', 'teammate@example.com')}
          value={email}
          onChange={e => setEmail(e.target.value)}
          errorText={error || undefined}
          autoFocus
        />

        <SelectField
          label={t('shareModal.roleLabel', 'Permission level')}
          name="shareRole"
          value={role}
          onChange={value => setRole(value as ShareRole)}
          options={roleOptions}
        />
      </div>
    </Modal>
  );
};
